<template>
  <b-modal
    id="AddUserModal"
    ref="AddUserModal"
    title="เพิ่มผู้ใช้"
    size="md"
    centered
    no-close-on-backdrop
    hide-footer
    @hidden="reset"
  >
    <!-- Stepper hint -->
    <div class="step-hint mb-3">
      <span :class="['step-dot', { on: step === 'identify' }]">1</span>
      <span class="step-line"></span>
      <span :class="['step-dot', { on: step === 'create' }]">2</span>
      <span class="step-line"></span>
      <span :class="['step-dot', { on: step === 'assign' }]">3</span>
      <span class="ml-2 text-muted small">{{ stepLabel }}</span>
    </div>

    <!-- ===================== STEP A: identify ===================== -->
    <div v-if="step === 'identify'">
      <b-form-group
        label="อีเมลผู้ใช้"
        description="ระบบจะค้นหาว่ามีบัญชีอยู่แล้วหรือไม่ ก่อนสร้างผู้ใช้ใหม่"
      >
        <b-input-group>
          <b-form-input
            v-model.trim="email"
            type="email"
            :state="emailFieldState"
            placeholder="name@example.com"
            data-testid="adduser-email-input"
            @keyup.enter="lookup"
          />
          <template #append>
            <b-button
              variant="primary"
              :disabled="!email || busy"
              data-testid="adduser-check-button"
              @click="lookup"
            >
              <b-spinner v-if="busy" small class="mr-1" />ตรวจสอบ
            </b-button>
          </template>
        </b-input-group>
      </b-form-group>
      <b-alert v-if="error" show variant="danger" class="mt-2 mb-0">{{ error }}</b-alert>
    </div>

    <!-- ===================== STEP B: create ====================== -->
    <div v-else-if="step === 'create'">
      <b-alert show variant="info" class="py-2 small">
        อีเมล <b>{{ email }}</b> ยังไม่มีในระบบ — กรุณากรอกข้อมูลเพื่อสร้างผู้ใช้ใหม่
      </b-alert>

      <b-row>
        <b-col cols="6">
          <b-form-group label="รหัสผ่าน">
            <b-form-input
              v-model="$v.registerForm.password.$model"
              :class="{ 'is-invalid': $v.registerForm.password.$error }"
              type="password"
              trim
              lazy
              data-testid="adduser-password"
            />
            <div v-if="$v.registerForm.password.$error" class="description error">
              <span v-if="!$v.registerForm.password.required">*กรุณาใส่รหัสผ่าน</span>
              <span v-else-if="!$v.registerForm.password.minLength">*รหัสผ่านต้องมีอย่างน้อย 8 ตัว</span>
              <span v-else-if="!$v.registerForm.password.passwordFormat">
                *ต้องมีตัวเลข (0-9) และตัวอักษรอังกฤษ (A-Z, a-z) อย่างน้อย 1 ตัว
              </span>
            </div>
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group label="ยืนยันรหัสผ่าน">
            <b-form-input
              v-model="$v.registerForm.confirmPassword.$model"
              :class="{ 'is-invalid': $v.registerForm.confirmPassword.$error }"
              type="password"
              trim
              lazy
              data-testid="adduser-confirm-password"
            />
            <div v-if="$v.registerForm.confirmPassword.$error" class="description error">
              <span v-if="!$v.registerForm.confirmPassword.required">*กรุณายืนยันรหัสผ่าน</span>
              <span v-else-if="!$v.registerForm.confirmPassword.sameAsPassword">*ไม่ตรงกับรหัสผ่าน</span>
            </div>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="6">
          <b-form-group label="ชื่อภาษาไทย">
            <b-form-input
              v-model="$v.registerForm.firstNameTh.$model"
              :class="{ 'is-invalid': $v.registerForm.firstNameTh.$error }"
              trim
              data-testid="adduser-firstname-th"
            />
            <div v-if="$v.registerForm.firstNameTh.$error" class="description error">
              *กรุณาใส่ชื่อภาษาไทย
            </div>
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group label="นามสกุลภาษาไทย">
            <b-form-input
              v-model="$v.registerForm.lastNameTh.$model"
              :class="{ 'is-invalid': $v.registerForm.lastNameTh.$error }"
              trim
              data-testid="adduser-lastname-th"
            />
            <div v-if="$v.registerForm.lastNameTh.$error" class="description error">
              *กรุณาใส่นามสกุลภาษาไทย
            </div>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="6">
          <b-form-group label="ชื่อภาษาอังกฤษ">
            <b-form-input
              v-model="$v.registerForm.firstNameEn.$model"
              :class="{ 'is-invalid': $v.registerForm.firstNameEn.$error }"
              trim
              data-testid="adduser-firstname-en"
            />
            <div v-if="$v.registerForm.firstNameEn.$error" class="description error">
              *กรุณาใส่ชื่อภาษาอังกฤษ
            </div>
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group label="นามสกุลภาษาอังกฤษ">
            <b-form-input
              v-model="$v.registerForm.lastNameEn.$model"
              :class="{ 'is-invalid': $v.registerForm.lastNameEn.$error }"
              trim
              data-testid="adduser-lastname-en"
            />
            <div v-if="$v.registerForm.lastNameEn.$error" class="description error">
              *กรุณาใส่นามสกุลภาษาอังกฤษ
            </div>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="4">
          <b-form-group label="คำนำหน้า (ไทย)">
            <b-form-select
              v-model="registerForm.titleTh"
              :options="titleThOptions"
              @change="handleTitleChange"
            />
          </b-form-group>
        </b-col>
        <b-col cols="4">
          <b-form-group label="Title (EN)">
            <b-form-select
              v-model="registerForm.titleEn"
              :options="titleEnOptions"
              @change="handleTitleChange"
            />
          </b-form-group>
        </b-col>
        <b-col cols="4">
          <b-form-group label="เพศ / Gender">
            <b-form-select v-model="registerForm.gender" :options="genderOptions" />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="6">
          <b-form-group label="เบอร์โทรศัพท์มือถือ">
            <b-form-input
              v-model="$v.registerForm.mobile.$model"
              :class="{ 'is-invalid': $v.registerForm.mobile.$error }"
              trim
              data-testid="adduser-mobile"
            />
            <div v-if="$v.registerForm.mobile.$error" class="description error">
              <span v-if="!$v.registerForm.mobile.required">*กรุณาใส่เบอร์โทรศัพท์</span>
              <span v-else-if="!$v.registerForm.mobile.digits">*เบอร์โทรศัพท์ต้องมีตัวเลข 10 ตัว</span>
            </div>
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group class="normal-weight" label="ประเภทผู้ใช้">
            <b-form-radio
              v-model="$v.registerForm.userTypeId.$model"
              name="adduser-user-type"
              :value="1"
              data-testid="adduser-usertype-staff"
            >
              บุคลากร (เคาน์เตอร์, ผู้จัดการ, ผู้ช่วย)
            </b-form-radio>
            <b-form-radio
              v-model="$v.registerForm.userTypeId.$model"
              name="adduser-user-type"
              :value="2"
              data-testid="adduser-usertype-dentist"
            >
              ทันตแพทย์
            </b-form-radio>
            <small class="text-danger d-block mt-1">* ไม่สามารถเปลี่ยนประเภทผู้ใช้ได้ภายหลัง</small>
            <div v-if="$v.registerForm.userTypeId.$error" class="description error">
              *กรุณาเลือกประเภทผู้ใช้
            </div>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row v-if="isDentist">
        <b-col cols="6">
          <b-form-group label="เลข ท.">
            <b-form-input
              v-model="$v.registerForm.drLicenseNo.$model"
              :class="{ 'is-invalid': $v.registerForm.drLicenseNo.$error }"
              type="number"
              trim
            />
            <div v-if="$v.registerForm.drLicenseNo.$error" class="description error">
              *กรุณาใส่เลข ท. (เฉพาะตัวเลข)
            </div>
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group label="สาขาเฉพาะทาง">
            <multiselect
              v-model="selectedSpecialist"
              :class="{ 'is-invalid': $v.registerForm.drSpecialItems.$error }"
              :options="specialistList"
              :multiple="true"
              track-by="val"
              label="label"
              :close-on-select="false"
              placeholder="เลือกสาขาเฉพาะทาง"
            />
            <div v-if="$v.registerForm.drSpecialItems.$error" class="description error">
              *กรุณาเลือกสาขาเฉพาะทาง
            </div>
          </b-form-group>
        </b-col>
      </b-row>

      <b-alert v-if="error" show variant="danger" class="mt-1 mb-2">{{ error }}</b-alert>

      <div class="d-flex justify-content-between mt-2">
        <b-button variant="link" class="px-0" :disabled="busy" @click="backToIdentify">
          <i class="fas fa-arrow-left mr-1"></i> ย้อนกลับ
        </b-button>
        <b-button
          variant="primary"
          :disabled="busy"
          data-testid="adduser-register-button"
          @click="create"
        >
          <b-spinner v-if="busy" small class="mr-1" />ลงทะเบียน
        </b-button>
      </div>
    </div>

    <!-- ===================== STEP C: assign ====================== -->
    <div v-else-if="step === 'assign'">
      <div class="target-strip mb-3">
        <div class="text-muted small">{{ alreadyInClinic ? "ผู้ใช้" : "มอบบทบาทแรกให้" }}</div>
        <div class="font-weight-bold">
          {{ user.firstNameTh }} {{ user.lastNameTh }}
          <span class="text-muted small font-weight-normal">· {{ email }}</span>
        </div>
      </div>

      <b-alert v-if="alreadyInClinic" show variant="info" class="py-2 small">
        ผู้ใช้นี้อยู่ในคลินิกนี้แล้ว — แก้ไขบทบาทเดิมได้จากรายการผู้ใช้ คุณยังเพิ่มบทบาทใหม่ได้จากที่นี่
      </b-alert>

      <b-form-group v-if="scopeOptions.length > 1" label="ขอบเขตของบทบาท" class="mb-2">
        <b-form-radio-group
          v-model="scope"
          :options="scopeOptions"
          buttons
          button-variant="outline-primary"
          size="sm"
          @change="roleKey = null"
        />
      </b-form-group>

      <b-form-group v-if="scope === 'branch'" label="สาขา" class="mb-3">
        <b-form-select v-model="branchUrl" :options="branchOptions" data-testid="adduser-branch-select">
          <template #first>
            <b-form-select-option :value="null" disabled>- เลือกสาขา -</b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>

      <b-form-group label="เลือกบทบาทที่จะมอบ" class="mb-3">
        <div v-if="roleChoices.length === 0" class="text-muted small">
          คุณไม่มีสิทธิ์มอบบทบาทใด ๆ ในขอบเขตนี้
        </div>
        <div v-else class="role-card-list">
          <button
            v-for="opt in roleChoices"
            :key="opt.value"
            type="button"
            class="role-card"
            :class="{ selected: roleKey === opt.value }"
            @click="roleKey = opt.value"
          >
            <span class="role-radio" :class="{ on: roleKey === opt.value }"></span>
            <span class="role-card-body">
              <span class="role-card-title">
                {{ opt.text }}
                <span class="text-muted small">· {{ opt.value }}</span>
              </span>
              <span v-if="opt.description" class="role-card-desc">{{ opt.description }}</span>
            </span>
          </button>
        </div>
      </b-form-group>

      <b-form-group
        label="เหตุผล (อย่างน้อย 10 ตัวอักษร — บันทึกไว้ในประวัติ)"
        label-class="font-weight-bold"
        :state="reasonState"
        :invalid-feedback="reasonError"
      >
        <b-form-textarea
          v-model="reason"
          rows="3"
          :state="reasonState"
          placeholder="ระบุเหตุผลในการมอบบทบาท..."
        />
        <div class="reason-templates mt-2">
          <b-badge
            v-for="tpl in reasonTemplates"
            :key="tpl"
            variant="light"
            class="reason-template-chip"
            @click="applyTemplate(tpl)"
          >
            + {{ tpl }}
          </b-badge>
        </div>
      </b-form-group>

      <b-alert v-if="error" show variant="danger" class="mt-2 mb-2">{{ error }}</b-alert>

      <div class="d-flex justify-content-end">
        <b-button
          variant="primary"
          :disabled="!canAssign || busy"
          data-testid="adduser-assign-button"
          @click="assign"
        >
          <b-spinner v-if="busy" small class="mr-1" />ยืนยัน
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import axios from "axios";
import {
  required,
  requiredIf,
  numeric,
  minLength,
  sameAs,
  helpers,
} from "vuelidate/lib/validators";
import { ROLE_LABELS_TH, ROLE_DESCRIPTIONS_TH } from "@/constants/roles";
import { eventBus } from "@/main";
import {
  getThaiTitleOptions,
  getEnglishTitleOptions,
  getGenderOptions,
  getTitleGenderSuggestion,
} from "@/utils/userTitleHelper";

const digits = number =>
  helpers.withParams(
    { type: "digits", value: number },
    value => !helpers.req(value) || helpers.len(value) == number
  );

const passwordFormat = helpers.regex(
  "passwordFormat",
  /^(?=.*[A-Za-z])(?=.*\d)[฀-๿A-Za-z\d@$!%*#?&]{1,}$/
);

function defaultRegisterForm() {
  return {
    firstNameTh: "",
    lastNameTh: "",
    firstNameEn: "",
    lastNameEn: "",
    email: "",
    mobile: "",
    password: "",
    confirmPassword: "",
    userTypeId: null,
    drLicenseNo: "",
    drSpecialItems: [],
    gender: "",
    titleTh: "",
    titleEn: "",
  };
}

export default {
  name: "AddUserModal",
  data() {
    return {
      // Passed in via show()
      clinicUrl: null,
      branches: [],

      // Flow
      step: "identify", // 'identify' | 'create' | 'assign'
      email: "",
      user: null, // { uid, firstNameTh, lastNameTh }
      alreadyInClinic: false, // resolved account is already a member of this clinic

      // Create
      registerForm: defaultRegisterForm(),
      selectedSpecialist: [],

      // Assign
      scope: "branch",
      branchUrl: null,
      roleKey: null,
      reason: "",
      reasonTemplates: ["จ้างใหม่", "เลื่อนตำแหน่ง", "ทดแทนชั่วคราว", "เพิ่มสาขาใหม่"],

      busy: false,
      error: "",

      titleThOptions: getThaiTitleOptions(),
      titleEnOptions: getEnglishTitleOptions(),
      genderOptions: getGenderOptions(),
    };
  },
  validations: {
    registerForm: {
      firstNameTh: { required },
      lastNameTh: { required },
      firstNameEn: { required },
      lastNameEn: { required },
      mobile: { required, numeric, digits: digits(10) },
      password: { required, minLength: minLength(8), passwordFormat },
      confirmPassword: { required, sameAsPassword: sameAs("password") },
      userTypeId: { required },
      drLicenseNo: {
        required: requiredIf(form => form.userTypeId === 2),
        numeric,
      },
      drSpecialItems: {
        required: requiredIf(form => form.userTypeId === 2),
      },
    },
  },
  computed: {
    ...mapGetters({
      assignableRoles: "moduleUser/assignableRoles",
      specialistList: "moduleMaster/getDrSpecialList",
    }),
    stepLabel() {
      return {
        identify: "ระบุอีเมล",
        create: "สร้างผู้ใช้ใหม่",
        assign: "มอบบทบาทแรก",
      }[this.step];
    },
    emailFieldState() {
      return this.error ? false : null;
    },
    isDentist() {
      return this.registerForm.userTypeId === 2;
    },
    scopeOptions() {
      const o = [];
      if (this.assignableRoles("clinic").length) {
        o.push({ text: "ระดับคลินิก", value: "clinic" });
      }
      if (this.assignableRoles("branch").length) {
        o.push({ text: "ระดับสาขา", value: "branch" });
      }
      return o;
    },
    branchOptions() {
      // `code` is the path segment the BE treats as {branchUrl} (see Clinic.vue
      // selectBatch → getBranchInfo(clinic.code, branch.code)).
      return this.branches.map(b => ({
        text: b.name || b.code,
        value: b.code,
      }));
    },
    roleChoices() {
      return this.assignableRoles(this.scope).map(k => ({
        value: k,
        text: ROLE_LABELS_TH[k] || k,
        description: ROLE_DESCRIPTIONS_TH[k] || "",
      }));
    },
    reasonState() {
      if (this.reason.length === 0) return null;
      return this.reason.trim().length >= 10;
    },
    reasonError() {
      if (this.reason.length > 0 && this.reason.trim().length < 10) {
        return "กรุณาระบุเหตุผลอย่างน้อย 10 ตัวอักษร";
      }
      return "";
    },
    canAssign() {
      return (
        !!this.roleKey &&
        this.reason.trim().length >= 10 &&
        (this.scope !== "branch" || !!this.branchUrl)
      );
    },
  },
  watch: {
    selectedSpecialist(val) {
      this.registerForm.drSpecialItems = (val || []).map(i => i.val);
    },
  },
  methods: {
    ...mapActions({
      registerUser: "moduleUser/registerUser",
      fetchDrSpecialList: "moduleMaster/fetchDrSpecialList",
    }),
    show({ clinicUrl, branches }) {
      this.reset();
      this.clinicUrl = clinicUrl;
      this.branches = branches || [];
      // Default scope: prefer the first available scope (clinic before branch).
      this.scope = this.scopeOptions[0] ? this.scopeOptions[0].value : "branch";
      // With a single branch there's nothing to choose — preselect it so the
      // branch scope is immediately submittable.
      if (this.branches.length === 1) {
        this.branchUrl = this.branches[0].code;
      }
      // Specialist options are needed only if a dentist is registered.
      if (!this.specialistList || this.specialistList.length === 0) {
        this.fetchDrSpecialList().catch(() => {});
      }
      this.$bvModal.show("AddUserModal");
    },
    backToIdentify() {
      this.step = "identify";
      this.error = "";
    },
    async lookup() {
      if (!this.email) return;
      this.busy = true;
      this.error = "";
      try {
        // Clinic-level lookup: resolves any existing account by email (works for
        // C_LEVEL/RDM admins, and finds users already in the clinic).
        const res = await axios.get(
          `/api/userMgmt/${this.clinicUrl}/user-lookup`,
          { params: { email: this.email }, skipGlobalErrorHandler: true }
        );
        const body = res.data || {};
        if (body.data) {
          // Existing account → go straight to assigning a role.
          this.user = body.data;
          this.alreadyInClinic = !!body.alreadyInClinic;
          this.step = "assign";
        } else {
          // No account → collect details to create one.
          this.registerForm = defaultRegisterForm();
          this.registerForm.email = this.email;
          this.$v.$reset();
          this.step = "create";
        }
      } catch (e) {
        // Stays on the identify step so a missing/unreachable endpoint (e.g. BE not
        // yet deployed) surfaces a retryable message instead of bricking the modal.
        this.error = "ตรวจสอบอีเมลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
      } finally {
        this.busy = false;
      }
    },
    async create() {
      this.$v.registerForm.$touch();
      if (this.$v.registerForm.$invalid) return;
      this.busy = true;
      this.error = "";
      // Backend OAS rejects gender=null despite nullable; omit when unset.
      const { confirmPassword, gender, ...rest } = this.registerForm;
      const payload = gender ? { ...rest, gender } : rest;
      try {
        const res = await this.registerUser(payload);
        const data = (res.data && res.data.data) || res.data || {};
        this.user = {
          uid: data.uid,
          firstNameTh: this.registerForm.firstNameTh,
          lastNameTh: this.registerForm.lastNameTh,
        };
        this.step = "assign";
      } catch (e) {
        const raw =
          (e.response && (e.response.data?.message || e.response.data?.errorMessage)) ||
          (typeof e.response?.data === "string" ? e.response.data : "") ||
          e.message ||
          "";
        if (/email has been used|email is existing|existing|used/i.test(raw)) {
          // An active account already owns this email — route back to look it up.
          this.error =
            "อีเมลนี้มีบัญชีที่ใช้งานอยู่แล้ว กรุณากด \"ตรวจสอบ\" เพื่อค้นหาผู้ใช้";
          this.step = "identify";
        } else {
          this.error = raw || "ลงทะเบียนผู้ใช้ไม่สำเร็จ";
        }
      } finally {
        this.busy = false;
      }
    },
    applyTemplate(tpl) {
      const base = this.reason.trim();
      this.reason = base ? `${base} ${tpl}` : tpl;
    },
    async assign() {
      if (!this.canAssign || this.busy) return;
      this.busy = true;
      this.error = "";
      const url =
        this.scope === "clinic"
          ? `/api/userMgmt/${this.clinicUrl}/central-members/${this.user.uid}/roles`
          : `/api/userMgmt/${this.clinicUrl}/branch/${this.branchUrl}/members/${this.user.uid}/roles`;
      try {
        await axios.post(
          url,
          { roleKey: this.roleKey, reason: this.reason.trim() },
          { skipGlobalErrorHandler: true }
        );
        eventBus.$emit("alertToast", {
          message: "เพิ่มผู้ใช้และมอบบทบาทสำเร็จ · ส่งอีเมลยืนยันแล้ว",
          variant: "success",
        });
        this.$emit("added");
        this.$bvModal.hide("AddUserModal");
      } catch (e) {
        // Keep the modal open with the resolved uid so the operator can retry
        // without creating a duplicate account (register + assign are non-atomic).
        this.error =
          e.response?.data?.errorMessage?.message ||
          e.response?.data?.errorMessage ||
          e.response?.data?.message ||
          e.message ||
          "มอบบทบาทไม่สำเร็จ";
      } finally {
        this.busy = false;
      }
    },
    handleTitleChange() {
      if (!this.registerForm.gender) {
        const suggested = getTitleGenderSuggestion(
          this.registerForm.titleTh,
          this.registerForm.titleEn
        );
        if (suggested) this.registerForm.gender = suggested;
      }
    },
    reset() {
      Object.assign(this.$data, this.$options.data.call(this));
      if (this.$v) this.$v.$reset();
    },
  },
};
</script>

<style scoped>
/* Stepper */
.step-hint {
  display: flex;
  align-items: center;
}
.step-dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #e9edf3;
  color: #8a94a6;
  font-size: 0.72rem;
  font-weight: 700;
}
.step-dot.on {
  background: #2f5b8c;
  color: #fff;
}
.step-line {
  width: 24px;
  height: 2px;
  background: #e3e8ef;
  margin: 0 4px;
}

.description.error {
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 0.2rem;
}

.normal-weight >>> label:not(.form-check-label):not(.custom-file-label) {
  font-weight: normal;
}

/* Assign step — mirrors AssignRoleDialog for visual consistency */
.target-strip {
  background: #f6f8fb;
  border: 1px solid #e3e8ef;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
}

.role-card-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.role-card {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  width: 100%;
  text-align: left;
  padding: 0.625rem 0.75rem;
  border: 1px solid #dce1e8;
  border-radius: 0.5rem;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.12s ease, background 0.12s ease, box-shadow 0.12s ease;
}
.role-card:hover {
  border-color: #b9c4d4;
}
.role-card.selected {
  border-color: #2f5b8c;
  background: #f0f5fb;
  box-shadow: 0 0 0 1px #2f5b8c inset;
}

.role-radio {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  border: 2px solid #b9c4d4;
  border-radius: 50%;
  display: grid;
  place-items: center;
}
.role-radio.on {
  border-color: #2f5b8c;
}
.role-radio.on::after {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #2f5b8c;
}

.role-card-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.role-card-title {
  font-weight: 600;
}
.role-card-desc {
  font-size: 0.8125rem;
  color: #6b7280;
  margin-top: 0.125rem;
}

.reason-templates {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}
.reason-template-chip {
  cursor: pointer;
  border: 1px solid #dce1e8;
  font-weight: 500;
}
.reason-template-chip:hover {
  background: #eef2f7;
}
</style>
